/**
 * PodTube Player - YouTube IFrame API wrapper with podcast controls & MediaSession API
 */

import { store } from "./store.js";

class PlayerController {
  constructor() {
    this.player = null;
    this.audioElement = null;
    this.currentVideo = null;
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 0;
    this.playbackRate = store.settings.playbackSpeed || 1.0;
    this.progressInterval = null;
    this.sleepTimerTimeout = null;
    this.isApiReady = false;
    this.isExpanded = false;
    this.awaitingPlay = false;

    this.onStateChangeCallbacks = new Set();
    this.initYouTubeApi();
    this.initAudioElement();
  }

  get isAudioMode() {
    return Boolean(this.currentVideo && this.currentVideo.audioUrl);
  }

  initAudioElement() {
    if (typeof window === "undefined" || typeof Audio === "undefined") return;
    if (this.audioElement) return;

    this.audioElement = new Audio();
    this.audioElement.preload = "auto";

    this.audioElement.addEventListener("play", () => {
      this.isPlaying = true;
      this.startProgressTracking();
      this.updateMediaSession();
      this.notify();
    });

    this.audioElement.addEventListener("pause", () => {
      this.isPlaying = false;
      this.stopProgressTracking();
      if (this.currentVideo && this.currentTime > 0) {
        store.updateProgress(this.currentVideo.videoId, this.currentTime, this.duration);
        store.saveListened(false);
      }
      this.notify();
    });

    this.audioElement.addEventListener("ended", () => {
      this.isPlaying = false;
      this.stopProgressTracking();
      if (this.currentVideo) {
        store.markAsListened(this.currentVideo.videoId, this.duration, this.duration);
        store.clearNowPlaying();
      }
      this.notify();
    });

    this.audioElement.addEventListener("loadedmetadata", () => {
      if (
        this.audioElement.duration &&
        !isNaN(this.audioElement.duration) &&
        isFinite(this.audioElement.duration)
      ) {
        this.duration = Math.floor(this.audioElement.duration);
        if (this.currentVideo) {
          store.saveVideoDuration(this.currentVideo.videoId, this.duration, false);
        }
      }
      this.notify();
    });

    this.audioElement.addEventListener("timeupdate", () => {
      if (!this.audioElement) return;
      this.currentTime = Math.floor(this.audioElement.currentTime);
      if (
        this.audioElement.duration &&
        !isNaN(this.audioElement.duration) &&
        isFinite(this.audioElement.duration)
      ) {
        this.duration = Math.floor(this.audioElement.duration);
      }
      if (this.currentVideo && this.currentTime > 0) {
        store.updateProgress(this.currentVideo.videoId, this.currentTime, this.duration);
      }
      this.notify();
    });

    this.audioElement.addEventListener("error", (e) => {
      console.warn("Audio playback error:", e);
      this.isPlaying = false;
      this.notify();
    });
  }

  initYouTubeApi() {
    if (
      typeof window !== "undefined" &&
      ("HAPPY_DOM" in window ||
        "__HAPPY_DOM__" in window ||
        navigator?.userAgent?.toLowerCase().includes("happydom"))
    ) {
      return;
    }
    if (window.YT && window.YT.Player) {
      this.isApiReady = true;
      return;
    }

    window.onYouTubeIframeAPIReady = () => {
      this.isApiReady = true;
      if (this.currentVideo && !this.player && !this.awaitingPlay) {
        this.createPlayer(this.currentVideo.videoId);
      }
    };

    try {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      const firstScriptTag = document.getElementsByTagName("script")[0];
      if (firstScriptTag && firstScriptTag.parentNode) {
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
      } else if (document.head || document.body) {
        (document.head || document.body).appendChild(tag);
      }
    } catch (_e) {}
  }

  createPlayer(videoId, startSeconds = 0) {
    const container = document.getElementById("yt-player-container");
    if (!container) return;

    const start = Math.max(0, Math.floor(startSeconds || this.currentTime || 0));

    if (this.player && typeof this.player.loadVideoById === "function") {
      try {
        this.player.loadVideoById({
          videoId: videoId,
          startSeconds: start,
        });
        if (this.playbackRate !== 1.0 && typeof this.player.setPlaybackRate === "function") {
          this.player.setPlaybackRate(this.playbackRate);
        }
        this.currentTime = start;
        this.isPlaying = true;
        this.startProgressTracking();
        this.notify();
        return;
      } catch (_e) {
        try {
          this.player.destroy();
        } catch {}
        this.player = null;
      }
    }

    this.player = new window.YT.Player("yt-player-container", {
      height: "100%",
      width: "100%",
      videoId: videoId,
      playerVars: {
        autoplay: 1,
        controls: 1,
        modestbranding: 1,
        rel: 0,
        playsinline: 1,
        enablejsapi: 1,
        origin: window.location.origin,
        start: start,
      },
      events: {
        onReady: (event) => this.onPlayerReady(event, start),
        onStateChange: (event) => this.onPlayerStateChange(event),
        onError: (event) => this.onPlayerError(event),
      },
    });
  }

  onPlayerReady(event, startSeconds = 0) {
    if (this.playbackRate !== 1.0 && typeof event.target.setPlaybackRate === "function") {
      event.target.setPlaybackRate(this.playbackRate);
    }
    const start = Math.max(0, Math.floor(startSeconds || this.currentTime || 0));
    if (start > 0 && typeof event.target.seekTo === "function") {
      try {
        event.target.seekTo(start, true);
      } catch {}
    }
    event.target.playVideo();
    this.startProgressTracking();
  }

  onPlayerStateChange(event) {
    // YT.PlayerState: -1 unstarted, 0 ended, 1 playing, 2 paused, 3 buffering, 5 cued
    if (event.data === window.YT.PlayerState.PLAYING) {
      this.isPlaying = true;
      this.duration =
        typeof this.player.getDuration === "function"
          ? this.player.getDuration() || this.duration
          : this.duration;
      if (this.currentVideo && this.duration > 0) {
        store.saveVideoDuration(this.currentVideo.videoId, this.duration, false);
      }
      this.startProgressTracking();
      this.updateMediaSession();
    } else if (event.data === window.YT.PlayerState.PAUSED) {
      this.isPlaying = false;
      this.stopProgressTracking();
      if (this.currentVideo && this.currentTime > 0) {
        store.updateProgress(this.currentVideo.videoId, this.currentTime, this.duration);
        store.saveListened(false);
      }
    } else if (event.data === window.YT.PlayerState.ENDED) {
      this.isPlaying = false;
      this.stopProgressTracking();
      if (this.currentVideo) {
        store.markAsListened(this.currentVideo.videoId, this.duration, this.duration);
        store.clearNowPlaying();
      }
    }

    this.notify();
  }

  onPlayerError(event) {
    console.warn("YouTube Player error code:", event.data);
    // If player errored during playback, attempt clean recovery using saved progress
    if (this.currentVideo && this.currentTime > 0) {
      store.updateProgress(this.currentVideo.videoId, this.currentTime, this.duration);
      store.saveListened(false);
    }
  }

  playVideo(videoItem) {
    if (!videoItem || !videoItem.videoId) return;

    const isAudio = Boolean(videoItem.audioUrl);

    // Stop/pause the other media engine when switching types
    if (isAudio) {
      if (this.player && typeof this.player.pauseVideo === "function") {
        try {
          this.player.pauseVideo();
        } catch {}
      }
    } else {
      if (this.audioElement && !this.audioElement.paused) {
        try {
          this.audioElement.pause();
        } catch {}
      }
    }

    // If it is already the current video, resume playback without restarting from zero
    if (this.currentVideo && this.currentVideo.videoId === videoItem.videoId) {
      if (isAudio && this.audioElement) {
        if (!this.audioElement.paused) return;
        this.audioElement.play().catch((_e) => {});
        this.isPlaying = true;
        this.notify();
        return;
      }
      if (!isAudio && this.player) {
        try {
          const state =
            typeof this.player.getPlayerState === "function" ? this.player.getPlayerState() : -1;
          if (state === window.YT.PlayerState.PLAYING) {
            return;
          } else if (
            state === window.YT.PlayerState.PAUSED ||
            state === window.YT.PlayerState.CUED ||
            state === window.YT.PlayerState.BUFFERING
          ) {
            const savedProgress = store.getProgress(videoItem.videoId) || this.currentTime || 0;
            const current =
              typeof this.player.getCurrentTime === "function"
                ? this.player.getCurrentTime() || 0
                : 0;
            if (current === 0 && savedProgress > 5) {
              try {
                this.player.seekTo(savedProgress, true);
              } catch {}
            }
            this.player.playVideo();
            this.isPlaying = true;
            this.startProgressTracking();
            this.notify();
            return;
          }
        } catch (_e) {}
      }
    }

    this.currentVideo = videoItem;
    this.awaitingPlay = false;
    store.setNowPlaying(videoItem);
    const savedProgress = store.getProgress(videoItem.videoId) || 0;
    this.currentTime = savedProgress;
    this.duration = videoItem.duration || store.getVideoDuration(videoItem.videoId) || 0;

    if (isAudio) {
      this.initAudioElement();
      if (this.audioElement) {
        if (this.audioElement.src !== videoItem.audioUrl) {
          this.audioElement.src = videoItem.audioUrl;
        }
        this.audioElement.playbackRate = this.playbackRate;
        if (savedProgress > 0) {
          this.audioElement.currentTime = savedProgress;
        }
        this.audioElement.play().catch((err) => {
          console.warn("Audio play prevented or errored:", err);
        });
        this.isPlaying = true;
      }
    } else {
      if (this.isApiReady) {
        this.createPlayer(videoItem.videoId, savedProgress);
      }
    }

    this.updateMediaSession();
    this.notify();
  }

  restore(videoItem) {
    if (!videoItem?.videoId) return;
    this.currentVideo = videoItem;
    this.currentTime = store.getProgress(videoItem.videoId);
    this.duration = videoItem.duration || store.getVideoDuration(videoItem.videoId) || 0;
    this.isPlaying = false;
    this.awaitingPlay = true;
    if (videoItem.audioUrl) {
      this.initAudioElement();
      if (this.audioElement) {
        this.audioElement.preload = "metadata";
        this.audioElement.src = videoItem.audioUrl;
        this.audioElement.playbackRate = this.playbackRate;
      }
    }
    this.notify();
  }

  togglePlayPause() {
    if (!this.currentVideo) return;

    if (this.isAudioMode) {
      this.initAudioElement();
      if (!this.audioElement) return;
      if (this.audioElement.paused) {
        const savedProgress = store.getProgress(this.currentVideo.videoId) || this.currentTime || 0;
        if (this.audioElement.currentTime === 0 && savedProgress > 5) {
          this.audioElement.currentTime = savedProgress;
        }
        this.audioElement.play().catch((_e) => {});
      } else {
        this.audioElement.pause();
      }
      return;
    }

    const savedProgress = store.getProgress(this.currentVideo.videoId) || this.currentTime || 0;

    this.awaitingPlay = false;
    if (!this.player) {
      if (this.isApiReady) this.createPlayer(this.currentVideo.videoId, savedProgress);
      return;
    }

    try {
      const state =
        typeof this.player.getPlayerState === "function" ? this.player.getPlayerState() : -1;
      if (state === window.YT.PlayerState.PLAYING) {
        this.player.pauseVideo();
      } else {
        const current =
          typeof this.player.getCurrentTime === "function" ? this.player.getCurrentTime() || 0 : 0;
        // If the player reset to 0 but we had saved progress (> 5s), restore position
        if (current === 0 && savedProgress > 5) {
          try {
            this.player.seekTo(savedProgress, true);
          } catch {}
        }
        this.player.playVideo();
      }
    } catch {
      this.createPlayer(this.currentVideo.videoId, savedProgress);
    }
  }

  seek(seconds) {
    const target = Math.max(0, Math.min(seconds, this.duration || 999999));
    this.currentTime = target;

    if (this.isAudioMode) {
      if (this.audioElement) {
        try {
          this.audioElement.currentTime = target;
        } catch {}
      }
      this.notify();
      return;
    }

    if (!this.player) return;
    try {
      this.player.seekTo(target, true);
      this.notify();
    } catch {}
  }

  skip(secondsDelta) {
    if (this.isAudioMode) {
      const current = this.audioElement ? this.audioElement.currentTime : this.currentTime;
      this.seek(current + secondsDelta);
      return;
    }
    if (!this.player) return;
    try {
      const current = this.player.getCurrentTime() || this.currentTime;
      this.seek(current + secondsDelta);
    } catch {}
  }

  setSpeed(rate) {
    this.playbackRate = rate;
    store.updateSettings({ playbackSpeed: rate });
    if (this.audioElement) {
      try {
        this.audioElement.playbackRate = rate;
      } catch {}
    }
    if (this.player && typeof this.player.setPlaybackRate === "function") {
      this.player.setPlaybackRate(rate);
    }
    this.notify();
  }

  cycleSpeed() {
    const rates = [1.0, 1.25, 1.5, 1.75, 2.0];
    const currentIndex = rates.indexOf(this.playbackRate);
    const nextRate = rates[(currentIndex + 1) % rates.length];
    this.setSpeed(nextRate);
  }

  startProgressTracking() {
    this.stopProgressTracking();
    let tickCount = 0;
    this.progressInterval = setInterval(() => {
      if (this.isAudioMode) {
        if (!this.audioElement) return;
        const time = Math.floor(this.audioElement.currentTime);
        const dur =
          this.audioElement.duration && !isNaN(this.audioElement.duration)
            ? Math.floor(this.audioElement.duration)
            : this.duration;
        this.currentTime = time;
        if (dur > 0) this.duration = dur;
        tickCount++;
        if (this.currentVideo && time > 0) {
          store.updateProgress(this.currentVideo.videoId, time, dur);
          if (tickCount % 5 === 0) {
            store.saveListened(false);
          }
        }
        this.notify();
        return;
      }

      if (!this.player) return;
      try {
        const time =
          typeof this.player.getCurrentTime === "function"
            ? this.player.getCurrentTime() || 0
            : this.currentTime;
        const dur =
          typeof this.player.getDuration === "function"
            ? this.player.getDuration() || this.duration
            : this.duration;

        this.currentTime = time;
        if (dur > 0) this.duration = dur;

        tickCount++;
        if (this.currentVideo && time > 0) {
          store.updateProgress(this.currentVideo.videoId, time, dur);
          // Persist to localStorage every 5 seconds to minimize flash writes
          if (tickCount % 5 === 0) {
            store.saveListened(false);
          }
        }

        this.notify();
      } catch {}
    }, 1000);
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  setSleepTimer(minutes) {
    if (this.sleepTimerTimeout) {
      clearTimeout(this.sleepTimerTimeout);
      this.sleepTimerTimeout = null;
    }

    if (!minutes || minutes <= 0) {
      return null;
    }

    this.sleepTimerTimeout = setTimeout(
      () => {
        if (this.isAudioMode && this.audioElement && this.isPlaying) {
          this.audioElement.pause();
        } else if (this.player && this.isPlaying) {
          this.player.pauseVideo();
        }
      },
      minutes * 60 * 1000
    );

    return minutes;
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    this.notify();
  }

  updateMediaSession() {
    if (!("mediaSession" in navigator) || !this.currentVideo) return;

    try {
      const artwork = [];
      if (this.currentVideo.thumbnail) {
        artwork.push({ src: this.currentVideo.thumbnail, sizes: "480x360", type: "image/jpeg" });
      }
      artwork.push({ src: "./icons/icon-512.png", sizes: "512x512", type: "image/png" });

      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.currentVideo.title,
        artist: this.currentVideo.channelTitle || this.currentVideo.author,
        album: this.isAudioMode ? "Podcast Feed" : "PodTube Podcast Feed",
        artwork,
      });

      navigator.mediaSession.setActionHandler("play", () => this.togglePlayPause());
      navigator.mediaSession.setActionHandler("pause", () => this.togglePlayPause());
      navigator.mediaSession.setActionHandler("seekbackward", () => this.skip(-15));
      navigator.mediaSession.setActionHandler("seekforward", () => this.skip(15));
    } catch {}
  }

  subscribe(callback) {
    this.onStateChangeCallbacks.add(callback);
    return () => this.onStateChangeCallbacks.delete(callback);
  }

  notify() {
    for (const cb of this.onStateChangeCallbacks) {
      try {
        cb(this);
      } catch (e) {
        console.error("Player state callback error:", e);
      }
    }
  }
}

export const player = new PlayerController();
